import { useState } from "react";
import { Eye, Download, RefreshCw, Trash2, FileText, Image as ImageIcon, Film } from "lucide-react";
import { toast } from "sonner";
import { API, api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const Icon = ({ f, size = 16 }) => {
  if (f.category === "image") return <ImageIcon size={size} className="text-[#7AD7C4]" />;
  if (f.category === "video") return <Film size={size} className="text-[#FF7D6E]" />;
  return <FileText size={size} className="text-[#5B52EB]" />;
};

export const DocPreviewDialog = ({ file, onClose }) => {
  if (!file) return null;
  const url = `${API}/files/${file.id}/download`;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="doc-preview-dialog">
        <DialogHeader>
          <DialogTitle className="truncate text-base">{file.name}</DialogTitle>
          <DialogDescription>v{file.version} · {file.uploaded_by_name}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-[50vh] bg-secondary/40 rounded-lg overflow-hidden flex items-center justify-center">
          {file.category === "image" && <img src={url} alt={file.name} className="max-h-[70vh] max-w-full object-contain" />}
          {file.category === "pdf" && <iframe title={file.name} src={url} className="w-full h-[70vh]" />}
          {file.category === "video" && <video src={url} controls className="max-h-[70vh] max-w-full" />}
          {!["image", "pdf", "video"].includes(file.category) && (
            <p className="text-sm text-muted-foreground p-6 text-center">Vista previa no disponible. Descarga el archivo para abrirlo.</p>
          )}
        </div>
        <div className="flex justify-end">
          <Button asChild size="sm" variant="outline"><a href={url} download={file.name}><Download size={14} className="mr-1" /> Descargar</a></Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DocRow = ({ file, onChanged, canWrite }) => {
  const [preview, setPreview] = useState(false);
  const url = `${API}/files/${file.id}/download`;

  const replace = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append("file", f);
    try {
      await api.post(`/files/${file.id}/replace`, form);
      toast.success("Documento reemplazado");
      onChanged();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async () => {
    if (!window.confirm(`¿Quitar "${file.name}" de esta ficha? Quedará en la papelera del Espacio.`)) return;
    try {
      await api.delete(`/files/${file.id}`);
      toast.success("Documento retirado");
      onChanged();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="flex items-center gap-2 rounded-lg border bg-card px-2.5 py-1.5 text-xs group" data-testid={`doc-row-${file.id}`}>
      <Icon f={file} />
      <button onClick={() => setPreview(true)} className="flex-1 min-w-0 text-left truncate hover:underline" title={file.name}>{file.name}</button>
      <span className="text-muted-foreground hidden sm:inline">v{file.version}</span>
      <button onClick={() => setPreview(true)} className="p-1 rounded hover:bg-secondary" title="Vista previa" data-testid={`doc-preview-${file.id}`}><Eye size={14} /></button>
      <a href={url} download={file.name} className="p-1 rounded hover:bg-secondary" title="Descargar" data-testid={`doc-download-${file.id}`}><Download size={14} /></a>
      {canWrite && (
        <>
          <label className="p-1 rounded hover:bg-secondary cursor-pointer" title="Reemplazar" data-testid={`doc-replace-${file.id}`}>
            <RefreshCw size={14} /><input type="file" className="hidden" onChange={replace} />
          </label>
          <button onClick={remove} className="p-1 rounded hover:bg-destructive/10 text-destructive" title="Quitar" data-testid={`doc-remove-${file.id}`}><Trash2 size={14} /></button>
        </>
      )}
      {preview && <DocPreviewDialog file={file} onClose={() => setPreview(false)} />}
    </div>
  );
};
